import { absoluteUrl, SITE_ORIGIN } from '../config/site';
import { paths } from '../routes/paths';

export const EMERGENCY_PATH = paths.emergency;

export const emergencySeo = {
  title: 'Emergency Dentist in Cusco | Same-Day Care | Smile Up',
  description:
    'Need an emergency dentist in Cusco? Same-day care for toothache, broken tooth, swelling or lost filling. English-speaking clinic at Av. La Cultura 822.',
};

export const emergencyFaqs = [
  {
    q: 'Do you speak English at the dental clinic?',
    a: 'Yes. Smile Up assists international patients in English and Spanish so you understand the diagnosis, options and what must be done now versus what can wait until you are home.',
  },
  {
    q: 'Can I see an emergency dentist in Cusco the same day?',
    a: 'We prioritize urgent pain, swelling, trauma and lost restorations whenever a slot is available — often the same day. Message us on WhatsApp with a short description and a photo if you can, and we will guide you on timing.',
  },
  {
    q: 'Where is the emergency dentist in Cusco?',
    a: 'Smile Up is at Av. La Cultura 822, Wanchaq, Cusco, Peru — a short ride from the historic center and most hotels. You can book online, WhatsApp us, or call the clinic.',
  },
  {
    q: 'What dental emergencies do you treat for tourists?',
    a: 'Common visits include severe toothache, broken or cracked teeth, lost fillings or crowns, gum or facial swelling, dental infections, braces or aligner problems, and knocks to the mouth after a fall.',
  },
  {
    q: 'How much does an emergency dental visit cost in Cusco?',
    a: 'Fees depend on the exam, any X-rays and the treatment needed to relieve pain or protect the tooth. We explain costs before we start so you can decide with a clear plan — especially if you are flying out soon.',
  },
  {
    q: 'What if I leave Cusco tomorrow?',
    a: 'We focus on stabilizing the problem: reduce pain, protect the tooth and give you written notes or records for your dentist at home or in your next city. Full cosmetic rebuilds can often wait; comfort and safety should not.',
  },
  {
    q: 'When should I go to a hospital instead of a dental clinic?',
    a: 'Go to a hospital emergency department first if you have trouble breathing or swallowing, rapidly spreading facial swelling, uncontrolled bleeding, or head trauma with dizziness or loss of consciousness. Those are medical emergencies, not only dental ones.',
  },
  {
    q: 'Can altitude in Cusco make a toothache worse?',
    a: 'Cusco sits at high altitude. Existing tooth or sinus pain can feel sharper, and sinus pressure can mimic a toothache. An exam helps tell dental infection apart from sinus-related discomfort so you are not treating the wrong problem.',
  },
];

export function buildEmergencyJsonLd(pathname = EMERGENCY_PATH) {
  const pageUrl = absoluteUrl(pathname);
  const clinicId = `${SITE_ORIGIN}/#dentist`;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Dentist',
        '@id': clinicId,
        name: 'Smile Up',
        url: SITE_ORIGIN,
        image: absoluteUrl('/icons/icon-512.png'),
        telephone: ['+51-84-607539', '+51-984-403369'],
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Av. La Cultura 822',
          addressLocality: 'Cusco',
          addressRegion: 'Cusco',
          addressCountry: 'PE',
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: -13.522669,
          longitude: -71.963983,
        },
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '10:00',
            closes: '19:00',
          },
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: 'Saturday',
            opens: '09:00',
            closes: '14:00',
          },
        ],
        availableLanguage: ['en', 'es'],
        medicalSpecialty: 'Dentistry',
        areaServed: [
          { '@type': 'City', name: 'Cusco' },
          { '@type': 'Country', name: 'Peru' },
        ],
        hasMap: 'https://maps.app.goo.gl/ZcuCg67p7StV3LBU7',
      },
      {
        '@type': 'MedicalWebPage',
        '@id': pageUrl,
        url: pageUrl,
        name: 'Emergency dentist in Cusco, Peru',
        description: emergencySeo.description,
        inLanguage: 'en',
        primaryImageOfPage: absoluteUrl('/og-emergency-dentist-cusco.png'),
        about: {
          '@type': 'MedicalProcedure',
          name: 'Emergency dental care',
          medicalSpecialty: 'Dentistry',
        },
        audience: {
          '@type': 'Audience',
          audienceType: 'International tourists and travelers in Cusco',
        },
        provider: { '@id': clinicId },
        mainEntity: { '@id': `${pageUrl}#faq` },
      },
      {
        '@type': 'FAQPage',
        '@id': `${pageUrl}#faq`,
        inLanguage: 'en',
        mainEntity: emergencyFaqs.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.a,
          },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: SITE_ORIGIN,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Emergency dentist in Cusco',
            item: pageUrl,
          },
        ],
      },
    ],
  };
}
